import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { toast } from 'react-toastify';
import React, { useEffect, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Alert,
  Divider,
  Grid,
  Typography,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  AlertTitle,
  Tooltip,
} from '@mui/material';
import { t } from 'i18next';
import { DialogCloseButton } from 'src/shared/components/organisms/dialogCloseButton';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { TransactionErrorActionEnum } from 'src/shared/models/enums/transactionErrorAction.enum';
import { TransactionActionEnum } from 'src/shared/models/enums/transactionAction.enum';
import { Close, ContentCopy, InfoOutlined, SyncProblem } from '@mui/icons-material';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import { getIncomingBankPaymentById, setIncomingBankPaymentAction } from 'src/shared/services/incomingBankPaymanetService';
import { getInvoiceGroupAccountsById } from 'src/shared/services/invoiceGroupService';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { useGlobals } from 'src/shared/contexts/appContext';
import { IncomingBankPayment } from '../models/incomingBankPayment';
import { IncomingBankPaymentActionConfirm } from './incomingBankPaymentActionConfirm';
import { IncomingBankPaymentPut } from '../models/incomingBankPaymentPut';
import { Invoice } from '../models/invoice';
import { InvoiceStatuses } from './invoiceStatuses';
import { InvoiceDetailSummary } from './invoiceDetailSummary';
import { InvoiceDeliveryChip } from './invoiceDeliveryChip';
import { IncomingBankPaymentTransferArrow } from './incomingBankPaymentTransferArrow';
import { IncomingBankPaymentEditOcr } from './incomingBankPaymentEditOcr';

type IncomingBankPaymentEditModalProps = {
  modalOpen: boolean;
  incomingBankPaymentId: number;
  ownerId?: number;
  onClose: () => void;
};

export const IncomingBankPaymentEditModal = (props: IncomingBankPaymentEditModalProps) => {
  const { modalOpen, incomingBankPaymentId, ownerId, onClose } = props;

  const { setDateStamp } = useGlobals();

  const [incomingBankPayment, setIncomingBankPayment] = useState<IncomingBankPayment>();

  const isMounted = useIsMounted();

  const [loading, setLoading] = useState<boolean>(true);

  // Fakturagruppens konto för överbetalning. Visas i tooltippen vid överskott, så att det framgår
  // var den överskjutande delen bokförs.
  const [excessAccount, setExcessAccount] = useState<string>('');

  const [transactionAction, setTransactionAction] = React.useState<TransactionActionEnum>(TransactionActionEnum.NoAction);

  const handlePaymentEditActionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = +(event.target as HTMLInputElement).value as TransactionActionEnum;
    setTransactionAction(newValue);
  };

  const [newInvoice, setNewInvoice] = useState<Invoice>();
  const [transactionErrorAction, setTransactionErrorAction] = React.useState<TransactionErrorActionEnum>(TransactionErrorActionEnum.Refund);
  const [transactionErrorActionDescription, setTransactionErrorActionDescription] = React.useState<string>();
  const handleTransactionErrorActionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTransactionErrorAction(+(event.target as HTMLInputElement).value as TransactionErrorActionEnum);
  };

  useEffect(() => {
    const fetchData = async () => {
      setTransactionAction(TransactionActionEnum.NoAction);
      setTransactionErrorAction(TransactionErrorActionEnum.Refund);
      setNewInvoice(undefined);
      try {
        const result = await getIncomingBankPaymentById(incomingBankPaymentId);
        if (isMounted()) {
          setIncomingBankPayment(result.data);
        }
      } catch (e) {
        if (isMounted()) {
          ConsoleHelper.log(e);
        }
      } finally {
        if (isMounted()) {
          setLoading(false);
        }
      }
    };
    fetchData();
  }, [isMounted, incomingBankPaymentId]);

  useEffect(() => {
    if (!ownerId) return;
    getInvoiceGroupAccountsById(ownerId)
      .then((result) => {
        if (isMounted()) setExcessAccount(result.data[0]?.excessAccount ?? '');
      })
      .catch((e) => {
        if (isMounted()) {
          ConsoleHelper.log(e);
          setExcessAccount('');
        }
      });
  }, [isMounted, ownerId]);

  const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false);

  const handleConfirm = async () => {
    setLoading(true);
    setShowConfirmationDialog(false);
    const data = {
      transactionId: incomingBankPayment?.transaction_id,
      action: transactionAction,
      errorAction:
        transactionAction === TransactionActionEnum.Incorrect || transactionAction === TransactionActionEnum.Extra ? transactionErrorAction : 0,
      comment: transactionAction === TransactionActionEnum.NewOcr ? newInvoice?.ocr : transactionErrorActionDescription,
      ownerId,
    } as IncomingBankPaymentPut;

    try {
      await setIncomingBankPaymentAction(data);
      if (isMounted()) {
        toast.success(t('invoice:incomingBankPayment.successMessage'));
        setDateStamp(new Date());
      }
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      setLoading(false);
      onClose();
    }
  };

  const componentId = `dialog-${Date.now().toString(36) + Math.random().toString(36).substring(2)}`;

  const onHandleClose = React.useCallback(() => {
    onClose();
  }, [onClose]);

  const showArrow = () => {
    return transactionAction === TransactionActionEnum.Incorrect || (transactionAction === TransactionActionEnum.NewOcr && !!newInvoice);
  };

  const ShowDetail = ({ label, value }) => {
    return (
      <Typography>
        <strong>{label}</strong>: {value}
      </Typography>
    );
  };

  const copyToClipboard = async (valueToCopy) => {
    try {
      await navigator.clipboard.writeText(valueToCopy);
      toast.success(<>{t('invoice:copyTextSuccess')}</>);
    } catch (error) {
      ConsoleHelper.error('Failed to copy text:', error);
    }
  };

  return (
    <>
      <Dialog
        onClose={onHandleClose}
        aria-labelledby={`${componentId}-titel`}
        open={modalOpen}
        maxWidth='md'
        fullWidth
        sx={{
          minWidth: {
            xs: '90vw',
            md: '400px',
          },
          '& .MuiDialogContent-root': {
            p: 0,
          },
          '& .MuiDialogActions-root': {
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ m: 0, py: 1.5, px: 2 }} id={`${componentId}-titel`}>
          {t('invoice:incomingBankPayment.editModalHeader')}
          <DialogCloseButton handleClose={onHandleClose} />
        </DialogTitle>
        <DialogContent dividers sx={{ pb: 0, minHeight: '15rem' }}>
          <LoadingOverlay loading={loading} />

          {incomingBankPayment && (
            <>
              <Alert severity='error' sx={{ borderRadius: 0 }}>
                {incomingBankPayment.stateText}
              </Alert>
              <Divider />
              <IncomingBankPaymentTransferArrow showArrow={showArrow()}>
                <GridWrapper>
                  <Grid item xs>
                    <Typography variant='h6'>{t('invoice:incomingBankPayment.paymentHeader')}</Typography>
                    <Typography sx={{ wordBreak: 'all' }}>{incomingBankPayment.sender}</Typography>
                    <Typography variant='h6' sx={{ mt: 2 }}>
                      {t('invoice:incomingBankPayment.paymentInfoHeader')}
                    </Typography>
                    <ShowDetail label={t('invoice:to')} value={`${incomingBankPayment.accountType} ${incomingBankPayment.primaryAccount}`} />
                    <ShowDetail label={t('invoice:amount')} value={incomingBankPayment.amount} />
                    <ShowDetail label={t('invoice:tranID')} value={incomingBankPayment.tranId} />
                    <ShowDetail label={t('invoice:date')} value={DateHelper.getDate(incomingBankPayment.date)} />
                    {incomingBankPayment.reference && (
                      <ShowDetail
                        label={t('invoice:reference')}
                        value={
                          <Tooltip title={t('common:button.copy')} placement='right' arrow>
                            <Button
                              variant='text'
                              onClick={() => copyToClipboard(incomingBankPayment.reference)}
                              endIcon={<ContentCopy sx={{ fontSize: '0.7rem' }} />}
                            >
                              {incomingBankPayment.reference}
                            </Button>
                          </Tooltip>
                        }
                      />
                    )}
                  </Grid>
                  <Grid item xs='auto'>
                    <Box sx={{ pl: 3, ml: 2, borderLeft: (thm) => `1px solid ${thm.palette.divider}` }}>
                      <Typography variant='h6'>{t('invoice:incomingBankPayment.transactionActionHeader')}</Typography>
                      <FormControl>
                        <RadioGroup value={transactionAction} onChange={handlePaymentEditActionChange}>
                          <FormControlLabel
                            value={TransactionActionEnum.NoAction}
                            control={<Radio />}
                            label={t('invoice:incomingBankPayment.transactionActionEnum.NoAction')}
                          />
                          {incomingBankPayment.state !== 0 && (
                            <FormControlLabel
                              value={TransactionActionEnum.Ok}
                              control={<Radio />}
                              // Varken en redan betald eller en makulerad faktura ska ta emot betalningen. Registreras den
                              // ändå körs allt som en betalning utlöser, som framflyttat förfallodatum, en gång till eller
                              // på något som medvetet makulerats. Handläggaren får i stället flytta betalningen med Ändra
                              // OCR referens eller klassa den med Hela betalningen är felaktig.
                              disabled={incomingBankPayment.state === 4 || incomingBankPayment.state === 5}
                              label={(() => {
                                // Modalen vet om betalningen är för liten eller för stor, och då säger etiketten det.
                                if (incomingBankPayment.state === 2) {
                                  return (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      {t('invoice:incomingBankPayment.transactionActionEnum.OkDeficit')}
                                      <Tooltip title={t('invoice:incomingBankPayment.transactionActionEnum.OkDeficitInfo') as string}>
                                        <InfoOutlined fontSize='small' color='action' />
                                      </Tooltip>
                                    </Box>
                                  );
                                }
                                // Läge 4, redan betald, och läge 5, makulerad, är låsta. Tooltippen säger varför.
                                if (incomingBankPayment.state === 4 || incomingBankPayment.state === 5) {
                                  const lockedInfo =
                                    incomingBankPayment.state === 4
                                      ? t('invoice:incomingBankPayment.transactionActionEnum.OkAlreadyPaidInfo')
                                      : t('invoice:incomingBankPayment.transactionActionEnum.OkCancelledInfo');
                                  return (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      {t('invoice:incomingBankPayment.transactionActionEnum.OkExact')}
                                      <Tooltip title={lockedInfo as string}>
                                        <InfoOutlined fontSize='small' color='action' />
                                      </Tooltip>
                                    </Box>
                                  );
                                }
                                // Läge 1, exakt belopp, har varken över- eller underskott.
                                if (incomingBankPayment.state !== 3) {
                                  return t('invoice:incomingBankPayment.transactionActionEnum.OkExact');
                                }
                                // Utan konto för överbetalning bokförs även den överskjutande delen som intäkt,
                                // och det behöver framgå lika tydligt som när ett konto finns.
                                const info = excessAccount
                                  ? t('invoice:incomingBankPayment.transactionActionEnum.OkExcessInfo', {
                                      account: excessAccount,
                                      amount: incomingBankPayment?.excessAmount ?? 0,
                                    })
                                  : t('invoice:incomingBankPayment.transactionActionEnum.OkExcessInfoNoAccount', {
                                      amount: incomingBankPayment?.excessAmount ?? 0,
                                    });
                                return (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {t('invoice:incomingBankPayment.transactionActionEnum.OkExcess')}
                                    <Tooltip title={info as string}>
                                      <InfoOutlined fontSize='small' color='action' />
                                    </Tooltip>
                                  </Box>
                                );
                              })()}
                            />
                          )}
                          {incomingBankPayment.state === 2 && (
                            <FormControlLabel
                              value={TransactionActionEnum.Rest}
                              control={<Radio />}
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  {t('invoice:incomingBankPayment.transactionActionEnum.OkLow')}
                                  <Tooltip title={t('invoice:incomingBankPayment.transactionActionEnum.OkLowInfo') as string}>
                                    <InfoOutlined fontSize='small' color='action' />
                                  </Tooltip>
                                </Box>
                              }
                            />
                          )}
                          {incomingBankPayment.state === 3 && (
                            <FormControlLabel
                              value={TransactionActionEnum.Extra}
                              control={<Radio />}
                              label={t('invoice:incomingBankPayment.transactionActionEnum.OkHigh')}
                            />
                          )}
                          <FormControlLabel
                            value={TransactionActionEnum.Incorrect}
                            control={<Radio />}
                            label={t('invoice:incomingBankPayment.transactionActionEnum.Incorrect')}
                          />
                          <FormControlLabel
                            value={TransactionActionEnum.NewOcr}
                            control={<Radio />}
                            label={t('invoice:incomingBankPayment.transactionActionEnum.NewOcr')}
                          />
                        </RadioGroup>
                      </FormControl>
                    </Box>
                  </Grid>
                </GridWrapper>
              </IncomingBankPaymentTransferArrow>
            </>
          )}
          {(transactionAction === TransactionActionEnum.Incorrect || transactionAction === TransactionActionEnum.Extra) && (
            <Box sx={{ p: 2 }} className='section-inset'>
              <Typography variant='h6'>{t('invoice:incomingBankPayment.changeIncorrectPaymentHeader')}</Typography>
              <GridWrapper>
                <Grid item xs={12}>
                  <FormControl>
                    <RadioGroup value={transactionErrorAction} onChange={handleTransactionErrorActionChange} row>
                      <FormControlLabel
                        value={TransactionErrorActionEnum.Refund}
                        control={<Radio />}
                        label={(() => {
                          // Valet gör olika saker beroende på vilken åtgärd som är vald, så etiketten följer den.
                          // Vid hela betalningen är felaktig registreras ingenting alls, och då heter valet det
                          // användaren faktiskt gör: ignorerar betalningen.
                          if (transactionAction === TransactionActionEnum.Incorrect) {
                            return (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {t('invoice:incomingBankPayment.transactionErrorEnum.Ignore')}
                                <Tooltip title={t('invoice:incomingBankPayment.transactionErrorEnum.IgnoreInfo') as string}>
                                  <InfoOutlined fontSize='small' color='action' />
                                </Tooltip>
                              </Box>
                            );
                          }

                          // Vid överskottet är felaktigt avgör kontot för överbetalning vad som händer med
                          // överskottet, och det ska framgå innan man väljer.
                          if (transactionAction !== TransactionActionEnum.Extra) {
                            return t('invoice:incomingBankPayment.transactionErrorEnum.Refund');
                          }

                          // Utan konto för överbetalning bokförs överskottet inte alls, precis som när hela
                          // betalningen är felaktig. Då ska valet heta samma sak på båda ställena.
                          if (!excessAccount) {
                            return (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {t('invoice:incomingBankPayment.transactionErrorEnum.Ignore')}
                                <Tooltip
                                  title={
                                    t('invoice:incomingBankPayment.transactionErrorEnum.RefundInfoNoAccount', {
                                      amount: incomingBankPayment?.excessAmount ?? 0,
                                    }) as string
                                  }
                                >
                                  <InfoOutlined fontSize='small' color='action' />
                                </Tooltip>
                              </Box>
                            );
                          }

                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {t('invoice:incomingBankPayment.transactionErrorEnum.Refund')}
                              <Tooltip
                                title={
                                  t('invoice:incomingBankPayment.transactionErrorEnum.RefundInfoExcess', {
                                    account: excessAccount,
                                    amount: incomingBankPayment?.excessAmount ?? 0,
                                  }) as string
                                }
                              >
                                <InfoOutlined fontSize='small' color='action' />
                              </Tooltip>
                            </Box>
                          );
                        })()}
                      />
                      <FormControlLabel
                        value={TransactionErrorActionEnum.Deposit}
                        control={<Radio />}
                        label={t('invoice:incomingBankPayment.transactionErrorEnum.Deposit')}
                      />
                      <FormControlLabel
                        value={TransactionErrorActionEnum.Memberfee}
                        control={<Radio />}
                        label={
                          // Den nya fakturan betalas direkt, och en fullbetald medlemsavgift förlänger medlemskapet
                          // oavsett belopp. Det ska framgå innan man väljer.
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {t('invoice:incomingBankPayment.transactionErrorEnum.Memberfee')}
                            <Tooltip
                              title={
                                (transactionAction === TransactionActionEnum.Extra
                                  ? t('invoice:incomingBankPayment.transactionErrorEnum.MemberfeeInfoExcess')
                                  : t('invoice:incomingBankPayment.transactionErrorEnum.MemberfeeInfo')) as string
                              }
                            >
                              <InfoOutlined fontSize='small' color='action' />
                            </Tooltip>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        value={TransactionErrorActionEnum.Purchase}
                        control={<Radio />}
                        label={t('invoice:incomingBankPayment.transactionErrorEnum.Purchase')}
                      />
                      <FormControlLabel
                        value={TransactionErrorActionEnum.Subscription}
                        control={<Radio />}
                        label={t('invoice:incomingBankPayment.transactionErrorEnum.Subscription')}
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t('invoice:incomingBankPayment.transactionErrorActionDescription')}
                    multiline
                    rows={4}
                    inputProps={{ maxLength: 95 }}
                    value={transactionErrorActionDescription}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setTransactionErrorActionDescription(event.target.value);
                    }}
                    fullWidth
                  />
                </Grid>
              </GridWrapper>
            </Box>
          )}
          {transactionAction === TransactionActionEnum.NewOcr && !newInvoice && (
            <IncomingBankPaymentEditOcr
              newOcrCallback={(invoice) => {
                setNewInvoice(invoice);
              }}
            />
          )}
          {transactionAction === TransactionActionEnum.NewOcr && newInvoice && (
            <Alert
              severity='info'
              className='section-inset'
              icon={<SyncProblem />}
              action={
                <Button size='small' onClick={() => setNewInvoice(undefined)}>
                  <Close />
                </Button>
              }
              sx={{ mb: 0, pt: 3, pb: 2, '& .MuiAlert-message': { width: '100%' } }}
            >
              <AlertTitle>{t('invoice:incomingBankPayment.newOcrHeader')}</AlertTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, '& .MuiTypography-h6': { lineHeight: '1 !important' } }}>
                <MainContentHeader title={`${t('dashboard:invoices.chartLabels.invoice')} #${newInvoice?.ocr}`} />
                {newInvoice?.status && (
                  <Box ml={1}>
                    <InvoiceStatuses status={newInvoice?.status} chipSize='small' />
                  </Box>
                )}
                <InvoiceDeliveryChip sendings={newInvoice?.sendings ?? []} chipSize='small' />
              </Box>
              <InvoiceDetailSummary invoice={newInvoice} />
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' color='primary' disableElevation onClick={onHandleClose}>
            {t('common:button.cancel')}
          </Button>
          <LoadingButton
            disabled={(transactionAction === TransactionActionEnum.NewOcr && !newInvoice) || loading}
            autoFocus
            variant='contained'
            type='submit'
            disableElevation
            loading={loading}
            onClick={() => setShowConfirmationDialog(true)}
          >
            {t('common:button.save')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
      <IncomingBankPaymentActionConfirm
        abort={() => setShowConfirmationDialog(false)}
        confirm={() => handleConfirm()}
        invoice={newInvoice}
        transactionAction={transactionAction}
        transactionErrorAction={transactionErrorAction}
        open={showConfirmationDialog}
      />
    </>
  );
};
